import moment from "moment";
import {
  collectClearableItemsFromEvents,
  dedupeClearableItems,
  getClearItemCounts,
  getClearPeriodDateRange,
  resolveDeleteRangeIsShift,
} from "./eventsWorkspaceClear.utils";

const EVENT_SHIFT_VIEW = {
  DAILY: "3",
  MONTHLY: "2",
  WEEKLY: "4",
};

/**
 * Inclusive calendar period for the immediately previous day/week/month.
 * @param {string} calendarViewId
 * @param {Date|string} date - Anchor in the currently selected period
 * @returns {{ startDate: string, endDate: string }}
 */
export function getPreviousPeriodDateRange(calendarViewId, date) {
  const anchor = moment(date);
  if (!anchor.isValid()) {
    return { startDate: "", endDate: "" };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.DAILY) {
    const day = anchor.clone().subtract(1, "day").format("YYYY-MM-DD");
    return { startDate: day, endDate: day };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.MONTHLY) {
    const prev = anchor.clone().subtract(1, "month");
    return {
      startDate: prev.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: prev.clone().endOf("month").format("YYYY-MM-DD"),
    };
  }

  const prev = anchor.clone().subtract(1, "week");
  return {
    startDate: prev.clone().startOf("week").format("YYYY-MM-DD"),
    endDate: prev.clone().endOf("week").format("YYYY-MM-DD"),
  };
}

/**
 * Period copy keys for the copy-previous confirmation modal.
 * @param {string} calendarViewId
 */
export function getCopyPreviousPeriodDetails(calendarViewId) {
  const titleKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.copyPreviousDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.copyPreviousWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.copyPreviousMonthly",
  };
  const introKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.copyPreviousIntroDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.copyPreviousIntroWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.copyPreviousIntroMonth",
  };
  const descriptionKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.copyPreviousDescriptionDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.copyPreviousDescriptionWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.copyPreviousDescriptionMonth",
  };
  const confirmQuestionKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.copyPreviousConfirmQuestionDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.copyPreviousConfirmQuestionWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.copyPreviousConfirmQuestionMonth",
  };

  return {
    titleKey: titleKeyByView[calendarViewId] || "scheduler.copyPreviousWeek",
    introKey: introKeyByView[calendarViewId] || "scheduler.copyPreviousIntroWeek",
    descriptionKey:
      descriptionKeyByView[calendarViewId] ||
      "scheduler.copyPreviousDescriptionWeek",
    confirmQuestionKey:
      confirmQuestionKeyByView[calendarViewId] ||
      "scheduler.copyPreviousConfirmQuestionWeek",
    confirmLabelKey: "scheduler.copyPreviousConfirm",
  };
}

/**
 * Whether an event matches the Event/Shift sidebar filter (same rules as views).
 * @param {{ isShift?: boolean, createdById?: number }} event
 * @param {string[]} eventShiftFilter
 * @param {number|null|undefined} profileId
 * @returns {boolean}
 */
export function matchesEventShiftFilter(
  event,
  eventShiftFilter = [],
  profileId = null,
) {
  if (!eventShiftFilter?.length) return true;

  const eventsFilter = eventShiftFilter.includes("event");
  const shiftFilter = eventShiftFilter.includes("shift");
  const shiftByOtherFilter = eventShiftFilter.includes("shift-others");
  const isShift = Boolean(event?.isShift);

  if (eventsFilter && !isShift) return true;
  if (
    shiftFilter &&
    isShift &&
    (shiftByOtherFilter || event?.createdById === profileId)
  ) {
    return true;
  }
  if (shiftByOtherFilter && isShift && event?.createdById !== profileId) {
    return true;
  }
  return false;
}

/**
 * Collect copyable items from ListForAssigner day rows, applying the same
 * visibility filters used by Event Workspace views.
 * @param {Array<{ schedulerEvents?: Array<object> }>} listData
 * @param {{
 *   eventShiftFilter?: string[],
 *   publishedDraftFilter?: string[],
 *   isEnterpriseFilterActive?: boolean,
 *   profileId?: number|null,
 * }} [filters]
 * @returns {Array<{ id: number, isShift: boolean }>}
 */
export function collectCopyableItemsFromListData(listData = [], filters = {}) {
  const {
    eventShiftFilter = [],
    publishedDraftFilter = [],
    isEnterpriseFilterActive = false,
    profileId = null,
  } = filters;

  const items = [];
  (listData || []).forEach((day) => {
    const events = (day?.schedulerEvents || []).filter((ev) => {
      if (!matchesEventShiftFilter(ev, eventShiftFilter, profileId)) {
        return false;
      }
      if (publishedDraftFilter?.length) {
        const isDraft = Boolean(ev?.isDraft);
        const matchesPublished =
          publishedDraftFilter.includes("published") && !isDraft;
        const matchesDraft =
          publishedDraftFilter.includes("draft") && isDraft;
        if (!matchesPublished && !matchesDraft) return false;
      }
      if (isEnterpriseFilterActive && ev?.isEnterprise !== true) {
        return false;
      }
      return true;
    });
    items.push(...collectClearableItemsFromEvents(events));
  });
  return dedupeClearableItems(items);
}

/**
 * Builds CopyRange request body (source = previous period, target = current).
 * @param {{
 *   ownerId: number,
 *   calendarViewId: string,
 *   currentDate: Date|string,
 *   copyableItems?: Array<{ isShift?: boolean }>,
 *   eventShiftFilter?: string[],
 *   officeIds?: number[],
 *   userIds?: number[],
 * }} options
 */
export function buildCopyPreviousPayload({
  ownerId,
  calendarViewId,
  currentDate,
  copyableItems = [],
  eventShiftFilter = [],
  officeIds = [],
  userIds = [],
}) {
  const source = getPreviousPeriodDateRange(calendarViewId, currentDate);
  const target = getClearPeriodDateRange(calendarViewId, currentDate);

  return {
    OwnerId: ownerId,
    SourceStartDate: source.startDate,
    SourceEndDate: source.endDate,
    TargetStartDate: target.startDate,
    TargetEndDate: target.endDate,
    IsShift: resolveDeleteRangeIsShift({
      clearableItems: copyableItems,
      eventShiftFilter,
    }),
    UserIds: userIds || [],
    OfficeIds: officeIds || [],
  };
}

export { getClearItemCounts as getCopyItemCounts, dedupeClearableItems };
